import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { getRowId } from '@texas/components/shared/samples/helper';
import { SampleLifecycleGroup } from '@texas/components/shared/samples/types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { samplesGenericColumns } from './columns/samplesGenericColumns';
import { samplesHandledColumns } from './columns/samplesHandledColumns';
import { samplesInititatedColumns } from './columns/samplesInititatedColumns';
import { samplesInTransportColumns } from './columns/samplesInTransportColumns';
import { SamplesWidgetContext } from './SamplesWidget';
import { defaultPageSize } from '@texas/components/shared/dataTable/defaultTableOptions';
import { DataTableContainerFooter } from '@texas/components/shared/dataTable/DataTableContainerFooter';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { samplesReceivedAtBrandColumns } from './columns/samplesReceivedAtBrandColumns';
import { TagFilterProvider } from '@texas/components/shared/tags/TagFilterContext';

export function SamplesTable({
  groupEnum,
}: {
  groupEnum: SampleLifecycleGroup;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const samplesContext = useActiveContext(SamplesWidgetContext);

  const columns: Record<string, any | undefined> = useMemo(() => {
    return {
      [SampleLifecycleGroup.UnhandledSamples]: samplesGenericColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
      [SampleLifecycleGroup.InInternalTransport]: samplesInTransportColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
      [SampleLifecycleGroup.InBrandTransport]: samplesInTransportColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
      [SampleLifecycleGroup.UnderwayFromSupplier]: samplesInititatedColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
      [SampleLifecycleGroup.ReceivedAtBrand]: samplesReceivedAtBrandColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
      [SampleLifecycleGroup.HandledSamples]: samplesHandledColumns({
        t,
        hideStopFollowing: samplesContext.hideStopFollowing,
      }),
    };
  }, [t, samplesContext.hideStopFollowing]);

  const { widgetId, persistColumnVisibility } = samplesContext;

  const samples = useMemo(() => {
    const offset = (page - 1) * defaultPageSize;
    return samplesContext.sampleGroups[groupEnum].slice(
      offset,
      offset + defaultPageSize,
    );
  }, [groupEnum, samplesContext, page]);

  return (
    <TagFilterProvider>
      <DataTableContainer
        w="100%"
        minH={0}
        display="flex"
        flexDir="column"
        error={samplesContext.error}
        css={{ ' th, td': { whiteSpace: 'normal' } }}
        tableId={
          persistColumnVisibility !== false
            ? `samples-${groupEnum}-${widgetId}`
            : undefined
        }
        datatable={{
          data: samples,
          columns:
            columns[groupEnum] ??
            columns[SampleLifecycleGroup.UnhandledSamples],
          isLoading: samplesContext.loading,
          selectionProps: {
            setSelectedRows: samplesContext.useMultiSelect.setSelectedRows,
            selectedRows: samplesContext.useMultiSelect.selectedRows,
          },
          rowProps: {
            getRowId: (originalRow, _) => {
              return getRowId(originalRow.sampleId);
            },
          },
          sorted: {
            onSortedChange: ({ key, desc }) => {
              samplesContext.setSearchQuery({
                ...samplesContext.searchQuery,
                sortBy: key,
                sortDesc: desc,
              });
            },
            key: samplesContext.searchQuery.sortBy,
            desc: samplesContext.searchQuery.sortDesc,
          },
        }}
        pagination={{
          totalItems: samplesContext.sampleGroups[groupEnum].length,
          pageSize: defaultPageSize,
          currentPage: page,
          onPageChange: (page) => setPage(page),
        }}
        footerContainerProps={{
          bg: 'texas.bg.900',
          _light: { bg: 'white' },
        }}
        footer={
          <DataTableContainerFooter
            selectedRows={
              Object.keys(samplesContext.useMultiSelect.selectedRows).length
            }
            onClear={() => samplesContext.useMultiSelect.setSelectedRows({})}
            text={t('sample.samplesSelected')}
          />
        }
      />
    </TagFilterProvider>
  );
}
